import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import PageContainer from '~/shared/components/PageContainer';
import Loader from '~/shared/components/Loader';
import { useBreadcrumb } from '~/shared/context/BreadcrumbContext';
import { useAuthContext } from '~/shared/context/useAuthContext';
import { toaster } from '~/shared/utils/toaster';
import { getOrgAccountsScopeValue } from '~/shared/utils/organizationUtils';

import UnsavedChangesModal from './ConfigurationDetails/UnsavedChangesModal';
import { ConfigurationTree } from './ConfigurationTree';
import { useConfigurationTree } from './ConfigurationTree/useConfigurationTree';
import { getStableAccountsKey } from './helpers/configurationApi';
import { useUnsavedChanges } from './hooks/useUnsavedChanges';

import type { ConfigTreeNode } from './types';

export type ConfigurationsContextType = {
  treeData: ConfigTreeNode | undefined;
  setHasUnsavedChanges: (hasChanges: boolean) => void;
  clearUnsavedChanges: () => void;
  setSaveChangesCallback: (callback: (() => () => Promise<void>) | null) => void;
};

export default function ConfigurationsPage() {
  const queryClient = useQueryClient();
  const { accounts, activeTenantId } = useAuthContext();
  const { nodeId } = useParams<{ nodeId?: string }>();
  const navigate = useNavigate();
  const [saveChangesCallback, setSaveChangesCallback] = useState<(() => Promise<void>) | null>(
    null
  );

  const { treeData, isLoading: isTreeLoading } = useConfigurationTree('edited');

  const {
    uiState,
    setHasUnsavedChanges,
    clearUnsavedChanges,
    hideModal,
    showModal,
    proceedWithNavigation,
  } = useUnsavedChanges();

  const { setBreadcrumbs } = useBreadcrumb();

  useEffect(() => {
    const crumbs = [
      { label: 'Settings', path: '/settings' },
      { label: 'Configurations', path: '/settings/configurations' },
    ];

    // if (nodeId) {
    //   crumbs.push({ label: nodeId, path: '' });
    // }

    setBreadcrumbs(crumbs);

    return () => setBreadcrumbs(null);
  }, [nodeId, setBreadcrumbs]);

  const stableAccountsKey = getStableAccountsKey(accounts ?? []);
  const accountsScope = getOrgAccountsScopeValue(accounts, activeTenantId) ?? 'self';

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ['configurationTree', 'edited', stableAccountsKey, accountsScope],
      exact: true,
    });
  }, [queryClient, stableAccountsKey, accountsScope]);

  const handleSaveAndNavigate = async () => {
    // Store the pending navigation before closing modal
    const pendingNav = uiState.pendingNavigation;

    // Close modal immediately
    hideModal();

    try {
      // Save changes
      if (saveChangesCallback) {
        await saveChangesCallback();
      }

      // Navigate after successful save
      if (pendingNav) {
        pendingNav();
        clearUnsavedChanges();
      }
    } catch (error) {
      console.error('Failed to save changes:', error);
      // If save failed, don't navigate
    }
  };

  const handleSelectNode = (newNodeId: string) => {
    if (newNodeId === nodeId) {
      return;
    }

    const doNavigate = () => {
      navigate(`/settings/configurations/${newNodeId}`);
      clearUnsavedChanges();
    };

    if (uiState.hasUnsavedChanges) {
      showModal(doNavigate);
    } else {
      doNavigate();
    }
  };

  const handleDiscardChanges = () => {
    toaster({
      title: 'Changes discarded',
      message: 'You exited the configuration without saving',
      variant: 'warning',
    });
    proceedWithNavigation();
  };

  return (
    <PageContainer className="p-0 pb-0! h-full flex flex-row overflow-hidden ">
      {isTreeLoading && !treeData ? (
        <div className="flex items-center justify-center w-full h-full">
          <Loader variant="xl" />
        </div>
      ) : (
        <>
          <div className="w-[355px] min-w-[355px] h-full border-r border-primary-70">
            <ConfigurationTree selectedNodeId={nodeId ?? ''} onSelectNode={handleSelectNode} />
          </div>

          <div className="flex-1 overflow-hidden">
            <Outlet
              context={
                {
                  treeData,
                  setHasUnsavedChanges,
                  clearUnsavedChanges,
                  setSaveChangesCallback,
                } satisfies ConfigurationsContextType
              }
            />
          </div>
        </>
      )}

      <UnsavedChangesModal
        isOpen={uiState.showUnsavedModal}
        onClose={handleDiscardChanges}
        onSave={handleSaveAndNavigate}
      />
    </PageContainer>
  );
}
